// The hedge pop-up's price feed for a Maven run.
//
// THE HEDGE POP-UP DOES NOT PRICE ITSELF. It reads `price` off the very objects it is handed —
// every position in a before/after state, every order in `balancedOrders`, every order in
// `orders` — and its host writes those prices on every quote tick, then publishes 'Hg.RefreshQuotes'
// so the (OnPush, detached) dialog redraws. The Hedging Grid does that inside its own component;
// Package Comparison does it through the APG's QuoteIndex. Maven handed the objects over as
// delivered and wrote nothing, so the dialog opened with every figure at $0.00 (owner, 2026-09-11).
//
// WHY NOT THE APG's QuoteIndex, which Package Comparison uses for the same dialog: its hedge branch
// signs a position ONLY when it carries both a group id AND a colour. The saved book carries a
// colour; the NEW hedges the ORION (OPG) delivers carry none (MavenHedgeShapeMapper stamps
// GroupId and Label, no Color). Through that index the opening legs would get no multiplier at all
// and render as NaN. So this index signs the way the dialog's OWNER does, in hedging-grid.component.ts:
//
//   position, before state        Math.sign(qty)
//   position, after state         Math.sign(qty) * -1   (unless the row is `noChanges`)
//   order (orders, balancedOrders) Math.sign(qty) * -1
//   price written                 quote.mid * multiplier
//
// ⚠ THREE SIGN CONVENTIONS LIVE IN THIS CODEBASE, one per object (ruling L62). This file carries the
// first two — position and order — because the dialog reads both. It carries NO fallback to bid/ask:
// the Hedging Grid has none for hedges, and a figure the owner's panel would not show is not one
// Maven's copy of its dialog should show.
//
// ⚠ TWO OBJECT GRAPHS, BOTH INDEXED. Over the wire `orders` and `balancedOrders` carry the same legs
// as independent objects (coordination.md §0c). The dialog renders rows from one and sums the other,
// so pricing one alone makes a table disagree with its own total. Both are indexed here, which is
// what closes that debt.
//
// This index MUTATES the delivered objects on purpose: the dialog is handed those objects, so the
// price has to be on them. It subscribes to nothing — the owning service subscribes the tickers it
// reports.

import {QuoteDto} from '../../shell-communication/dtos/quote-dto.class';
import {HedgeStateTransaction} from '../../hedging-grid/data-model/hedge-state-transaction';
import {HedgeOrder} from '../../hedging-grid/data-model/hedge-order';
import {HedgePosition} from '../../hedging-grid/data-model/hedge-position';

interface IndexedLeg {
    data: HedgePosition | HedgeOrder;
    multiplier: number;
}

export class MavenHedgeQuoteIndex {

    private _index: { [ticker: string]: IndexedLeg[] } = {};

    /** Every ticker indexed so far, de-duplicated. The subscription list for the owner. */
    get tickers(): string[] {
        return Object.keys(this._index);
    }

    /** Index every leg of the given transactions. Safe on partial shapes: a missing part is skipped. */
    add(transactions: HedgeStateTransaction[] | null | undefined): void {

        (transactions || []).forEach(transaction => {

            if (!transaction) {
                return;
            }

            (transaction.positionTransactions || []).forEach(row => {

                if (!row) {
                    return;
                }

                const before = row.beforeState ? row.beforeState.positions : null;
                const after = row.afterState ? row.afterState.positions : null;

                (before || []).forEach(pos => this.put(pos, Math.sign(pos.qty)));

                (after || []).forEach(pos => this.put(pos, row.noChanges ? Math.sign(pos.qty) : Math.sign(pos.qty) * -1));
            });

            (transaction.orderTransactions || []).forEach(group => {

                if (!group) {
                    return;
                }

                (group.orders || []).forEach(order => this.put(order, Math.sign(order.qty) * -1));

                (group.balancedOrders || []).forEach(pkg => {
                    (pkg || []).forEach(order => this.put(order, Math.sign(order.qty) * -1));
                });
            });
        });
    }

    /** Write the mid onto every indexed leg the quotes touch. Returns true when anything changed. */
    onQuote(quotes: QuoteDto[] | null | undefined): boolean {

        let touched = false;

        (quotes || []).forEach(quote => {

            if (!quote || !quote.ticker) {
                return;
            }

            const legs = this._index[quote.ticker];

            if (!legs || legs.length === 0) {
                return;
            }

            legs.forEach(leg => {
                leg.data.price = quote.mid * leg.multiplier;
            });

            touched = true;
        });

        return touched;
    }

    reset(): void {
        this._index = {};
    }

    private put(leg: HedgePosition | HedgeOrder, multiplier: number): void {

        if (!leg || !leg.ticker) {
            return;
        }

        let legs = this._index[leg.ticker];

        if (!legs) {
            legs = [];
            this._index[leg.ticker] = legs;
        }

        legs.push({data: leg, multiplier});
    }
}
